import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Calendar as CalIcon, Sparkles, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDateLocal(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Booking() {
  const [services, setServices] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [unavailableTimes, setUnavailableTimes] = useState([]);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    add_ons: [],
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    address: "",
    notes: "",
    time: "",
  });
  const [date, setDate] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // booking confirmation

  useEffect(() => {
    axios
      .get(`${API}/services`)
      .then((r) => {
        setServices(r.data.services || []);
        setAddOns(r.data.add_ons || []);
        setTimeSlots(r.data.time_slots || []);
      })
      .catch(() => {
        toast.error("Couldn't load services. Please refresh.");
      });

    try {
      const pre = sessionStorage.getItem("preselect_service");
      if (pre) {
        setForm((f) => ({ ...f, service: pre }));
        sessionStorage.removeItem("preselect_service");
      }
    } catch {}
  }, []);

  // Fetch availability when date changes
  useEffect(() => {
    if (!date) return;
    const dStr = formatDateLocal(date);
    axios
      .get(`${API}/bookings/availability`, { params: { date: dStr } })
      .then((r) => {
        const taken = r.data.taken || [];
        setUnavailableTimes(taken);
        if (taken.includes(form.time)) {
          setForm((f) => ({ ...f, time: "" }));
        }
      })
      .catch(() => setUnavailableTimes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const selectedService = useMemo(
    () => services.find((s) => s.id === form.service),
    [services, form.service]
  );

  const total = useMemo(() => {
    const base = selectedService?.price || 0;
    const adds = form.add_ons.reduce((sum, id) => {
      const a = addOns.find((x) => x.id === id);
      return sum + (a?.price || 0);
    }, 0);
    return base + adds;
  }, [selectedService, form.add_ons, addOns]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleAddOn = (id) => {
    setForm((f) => ({
      ...f,
      add_ons: f.add_ons.includes(id)
        ? f.add_ons.filter((x) => x !== id)
        : [...f.add_ons, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !date || !form.time ||
        !form.vehicle_make || !form.vehicle_model || !form.address) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        date: formatDateLocal(date),
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      setSuccess(data);
      toast.success("Booking received! We'll confirm shortly.");
      setForm({
        name: "", phone: "", email: "", service: "", add_ons: [],
        vehicle_make: "", vehicle_model: "", vehicle_year: "",
        address: "", notes: "", time: "",
      });
      setDate(undefined);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Try calling us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section id="booking" className="relative py-20 md:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-2xl border border-cyan-500/40 bg-zinc-900/40 backdrop-blur-md p-10 md:p-14 text-center shadow-[0_0_60px_rgba(34,211,238,0.15)]"
            data-testid="booking-success"
          >
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-cyan-400 text-zinc-950 mb-6">
              <Check className="h-8 w-8" strokeWidth={3} />
            </div>
            <h3 className="text-3xl md:text-4xl font-black tracking-tighter">Booking received.</h3>
            <p className="mt-3 text-zinc-400">
              Thanks {success.name?.split(" ")[0] || ""}! We'll text you to confirm your{" "}
              <span className="text-cyan-400 font-semibold">{success.service_label}</span> on{" "}
              <span className="text-cyan-400 font-semibold">{success.date}</span> at{" "}
              <span className="text-cyan-400 font-semibold">{success.time}</span>.
            </p>
            <div className="mt-8 inline-flex flex-col sm:flex-row gap-3">
              <a
                href="tel:+17079090231"
                className="px-6 py-3 rounded-full bg-cyan-400 text-zinc-950 font-bold hover:bg-cyan-300 transition-all"
              >
                Call (707) 909-0231
              </a>
              <button
                onClick={() => setSuccess(null)}
                data-testid="new-booking-btn"
                className="px-6 py-3 rounded-full border border-zinc-700 text-zinc-200 font-semibold hover:border-cyan-400 hover:text-cyan-400 transition-all"
              >
                Make another booking
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="relative py-20 md:py-32 border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-3 flex items-center gap-2">
            <CalIcon className="h-3.5 w-3.5" /> Reserve a slot
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter">
            Book your detail.
          </h2>
          <p className="mt-4 text-zinc-400">
            Pick a service, choose a date and time. We'll text you to confirm.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="mt-12 grid lg:grid-cols-12 gap-8"
          data-testid="booking-form"
        >
          {/* Left: form */}
          <div className="lg:col-span-7 space-y-8">
            {/* Service selection */}
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 backdrop-blur-md p-6 md:p-8">
              <h3 className="text-lg font-semibold mb-4">1. Select Service</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                {services.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => update("service", s.id)}
                    data-testid={`select-service-${s.id}`}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      form.service === s.id
                        ? "border-cyan-400 bg-cyan-400/5 shadow-[0_0_20px_rgba(34,211,238,0.15)]"
                        : "border-zinc-800 hover:border-zinc-600"
                    }`}
                  >
                    <div className="font-semibold text-sm">{s.label}</div>
                    <div className="text-2xl font-black text-cyan-400 mt-1">${s.price}</div>
                  </button>
                ))}
              </div>

            </div>

            {/* Date & time */}
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 backdrop-blur-md p-6 md:p-8">
              <h3 className="text-lg font-semibold mb-4">2. Pick Date &amp; Time</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm text-zinc-400">Date</Label>
                  <div className="mt-2 rounded-xl border border-zinc-800 bg-zinc-950/60 p-2 inline-block" data-testid="booking-calendar">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return d < today;
                      }}
                      className="text-zinc-100"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-zinc-400">Time slot</Label>
                  <div className="mt-2 grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                    {timeSlots.map((t) => {
                      const taken = unavailableTimes.includes(t);
                      const selected = form.time === t;
                      return (
                        <button
                          key={t}
                          type="button"
                          disabled={taken || !date}
                          onClick={() => update("time", t)}
                          data-testid={`time-${t.replace(/\s|:/g, "")}`}
                          className={`px-3 py-2.5 text-sm rounded-lg border transition-all ${
                            selected
                              ? "border-cyan-400 bg-cyan-400 text-zinc-950 font-bold"
                              : taken
                              ? "border-zinc-900 bg-zinc-900/30 text-zinc-600 cursor-not-allowed line-through"
                              : !date
                              ? "border-zinc-900 text-zinc-600 cursor-not-allowed"
                              : "border-zinc-800 text-zinc-200 hover:border-cyan-400 hover:text-cyan-400"
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                  {!date && (
                    <p className="mt-3 text-xs text-zinc-500">Pick a date to see available times.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Customer + vehicle */}
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 backdrop-blur-md p-6 md:p-8 space-y-5">
              <h3 className="text-lg font-semibold">3. Your Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-sm text-zinc-400">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="input-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 focus-visible:ring-cyan-400 focus-visible:border-cyan-400"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm text-zinc-400">Phone *</Label>
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 focus-visible:ring-cyan-400 focus-visible:border-cyan-400"
                    placeholder="(707) 555-0100"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="email" className="text-sm text-zinc-400">Email (optional)</Label>
                  <Input
                    id="email"
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 focus-visible:ring-cyan-400 focus-visible:border-cyan-400"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="make" className="text-sm text-zinc-400">Vehicle Make *</Label>
                  <Input
                    id="make"
                    data-testid="input-make"
                    value={form.vehicle_make}
                    onChange={(e) => update("vehicle_make", e.target.value)}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 focus-visible:ring-cyan-400 focus-visible:border-cyan-400"
                    placeholder="Toyota"
                  />
                </div>
                <div>
                  <Label htmlFor="model" className="text-sm text-zinc-400">Vehicle Model *</Label>
                  <Input
                    id="model"
                    data-testid="input-model"
                    value={form.vehicle_model}
                    onChange={(e) => update("vehicle_model", e.target.value)}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 focus-visible:ring-cyan-400 focus-visible:border-cyan-400"
                    placeholder="Camry"
                  />
                </div>
                <div>
                  <Label htmlFor="year" className="text-sm text-zinc-400">Year (optional)</Label>
                  <Input
                    id="year"
                    data-testid="input-year"
                    value={form.vehicle_year}
                    onChange={(e) => update("vehicle_year", e.target.value)}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 focus-visible:ring-cyan-400 focus-visible:border-cyan-400"
                    placeholder="2021"
                  />
                </div>
                <div>
                  <Label htmlFor="address" className="text-sm text-zinc-400">Service Address *</Label>
                  <Input
                    id="address"
                    data-testid="input-address"
                    value={form.address}
                    onChange={(e) => update("address", e.target.value)}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 focus-visible:ring-cyan-400 focus-visible:border-cyan-400"
                    placeholder="Santa Rosa, CA"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="notes" className="text-sm text-zinc-400">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="input-notes"
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 focus-visible:ring-cyan-400 focus-visible:border-cyan-400 resize-none"
                    rows={3}
                    placeholder="Pet hair on back seats, coffee stain on driver mat, etc."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right: summary */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 rounded-2xl border border-zinc-800 bg-zinc-900/40 backdrop-blur-md p-6 md:p-8">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-cyan-400" />
                Booking Summary
              </h3>

              <div className="space-y-3 text-sm">
                <Row label="Service" value={selectedService?.label || "—"} />
                <Row label="Date" value={date ? formatDateLocal(date) : "—"} />
                <Row label="Time" value={form.time || "—"} />
                <Row
                  label="Add-ons"
                  value={
                    form.add_ons.length
                      ? form.add_ons
                          .map((id) => addOns.find((a) => a.id === id)?.label)
                          .filter(Boolean)
                          .join(", ")
                      : "None"
                  }
                />
              </div>

              <div className="mt-6 pt-6 border-t border-zinc-800 space-y-2 text-sm">
                {selectedService && (
                  <div className="flex items-center justify-between text-zinc-400">
                    <span>{selectedService.label}</span>
                    <span>${selectedService.price}</span>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-zinc-800 flex items-baseline justify-between">
                <span className="text-zinc-400 text-sm uppercase tracking-wider">Estimated Total</span>
                <span
                  className="text-4xl font-black tracking-tighter text-cyan-400"
                  data-testid="booking-total"
                >
                  ${total}
                </span>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-booking-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-cyan-400 text-zinc-950 font-bold hover:bg-cyan-300 transition-all shadow-[0_0_30px_rgba(34,211,238,0.3)] hover:shadow-[0_0_40px_rgba(34,211,238,0.6)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Submitting...
                  </>
                ) : (
                  <>Confirm Booking</>
                )}
              </button>

              <p className="mt-4 text-xs text-zinc-500 text-center">
                No payment required now. We confirm via text/call before service.
              </p>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-zinc-500 shrink-0">{label}</span>
      <span className="text-zinc-200 text-right">{value}</span>
    </div>
  );
}
